<template>
  <div>
    <error-box v-if="error" @reload="load()" />

    <loader v-else-if="loading" />

    <template v-if="!error && !loading">
      <panel :title="$tc('tournament.general_info')">
        <main-input
          v-model="form.name"
          :label="$tc('tournament.tournament_name')"
          :validator="$v.form.name"
        />
        <main-textarea
          v-model="form.description"
          :label="$tc('form.description')"
          :validator="$v.form.description"
        />
        <el-row :gutter="16">
          <el-col :span="12">
            <main-date-picker
              v-model="form.startDate"
              type="datetime"
              :label="$tc('tournament.registration_start_date')"
              :validator="$v.form.startDate"
            />
          </el-col>
          <el-col :span="12">
            <main-date-picker
              v-model="form.endDate"
              type="datetime"
              :label="$tc('tournament.registration_end_date')"
              :validator="$v.form.endDate"
            />
          </el-col>
        </el-row>
        <main-checkbox
          v-model="form.chargeAllDiscplines"
          :label="$tc('tournament.charge_all_disciplines')"
        />
      </panel>

      <panel :title="$tc('settings.discipline', 2)">
        <main-select
          v-model="form.disciplines"
          :label="$tc('tournament.single_disciplines')"
          :options="disciplineOptions"
          :validator="$v.form.disciplines"
          multiple
          clearable
        />
        <main-select
          v-model="form.teamDisciplines"
          :label="$tc('tournament.team_disciplines')"
          :options="teamDisciplineOptions"
          :validator="$v.form.teamDisciplines"
          multiple
          clearable
        />
      </panel>

      <panel :title="$tc('tournament.scoring')">
        <div class="scoring-grid">
          <div class="scoring-labels">
            <span>{{ $tc('tournament.first_place_points') }}</span>
            <span>{{ $tc('tournament.second_place_points') }}</span>
            <span>{{ $tc('tournament.third_place_points') }}</span>
            <span>{{ $tc('tournament.fourth_place_points') }}</span>
            <span>{{ $tc('tournament.other_placements_points') }}</span>
          </div>
          <div class="scoring-inputs">
            <main-input
              v-model.number="form.firstPlacePoints"
              type="number"
              label=""
              :validator="$v.form.firstPlacePoints"
              :min="0"
              step="1"
            />
            <main-input
              v-model.number="form.secondPlacePoints"
              type="number"
              label=""
              :validator="$v.form.secondPlacePoints"
              :min="0"
              step="1"
            />
            <main-input
              v-model.number="form.thirdPlacePoints"
              type="number"
              label=""
              :validator="$v.form.thirdPlacePoints"
              :min="0"
              step="1"
            />
            <main-input
              v-model.number="form.fourthPlacePoints"
              type="number"
              label=""
              :validator="$v.form.fourthPlacePoints"
              :min="0"
              step="1"
            />
            <main-input
              v-model.number="form.otherPlacementsPoints"
              type="number"
              label=""
              :validator="$v.form.otherPlacementsPoints"
              :min="0"
              step="1"
            />
          </div>
        </div>
      </panel>

      <panel :title="$tc('tournament.certificates')">
        <document-types-panel :tournament-id="tournamentId || (tournament && tournament.id)" />
      </panel>

      <panel :title="$tc('tournament.files_and_images')">
        <main-upload-input
          v-model="cover"
          :label="$tc('general.cover_image')"
        />
        <main-upload-input
          v-model="images"
          :label="$tc('general.images')"
          multiple
        />
        <main-upload-input
          v-model="documents"
          :label="$tc('general.documents')"
          multiple
        />
      </panel>

      <panel
        v-if="!isEdit || $feature.registration_fee"
        :title="$tc('general.registration_fee')"
      >
        <div class="fee-type-selector">
          <main-select
            v-model="form.registration_fee_type"
            :label="$tc('tournament.registration_fee_type')"
            :options="registrationFeeTypeOptions"
            :validator="$v.form.registration_fee_type"
            :placeholder="$tc('tournament.registration_fee_type_placeholder')"
            clearable
            class="fee-type-select"
          />
        </div>

        <template v-if="form.registration_fee_type === 'legacy'">
          <el-table :data="registrationFeeData" style="width: 100%">
            <el-table-column :label="$tc('form.description')">
              <template slot-scope="scope">
                {{ scope.row.desc }}
              </template>
            </el-table-column>
            <el-table-column :label="$tc('form.price')" width="200px">
              <template slot-scope="scope">
                <main-input
                  v-model="form[scope.row.key]"
                  :validator="$v.form[scope.row.key]"
                  :append="$config.currency"
                />
              </template>
            </el-table-column>
            <el-table-column
              :label="$tc('form.discount')"
              width="170px"
              align="right"
            >
              <template slot-scope="scope">
                <main-input
                  v-model="form[scope.row.break_kee]"
                  :validator="$v.form[scope.row.break_kee]"
                  :append="$config.currency"
                />
              </template>
            </el-table-column>
          </el-table>
        </template>

        <template v-if="form.registration_fee_type === 'per_discipline'">
          <el-table :data="registrationFeeDataPerDiscipline" style="width: 100%">
            <el-table-column :label="$tc('form.description')">
              <template slot-scope="scope">
                {{ scope.row.desc }}
              </template>
            </el-table-column>
            <el-table-column :label="$tc('form.price')" width="200px">
              <template slot-scope="scope">
                <main-input
                  v-model.number="form[scope.row.key]"
                  :validator="$v.form[scope.row.key]"
                  type="number"
                  :min="0"
                  :append="$config.currency"
                />
              </template>
            </el-table-column>
            <el-table-column
              :label="$tc('form.discount')"
              width="170px"
              align="right"
            >
              <template slot-scope="scope">
                <main-input
                  v-model.number="form[scope.row.break_kee]"
                  :validator="$v.form[scope.row.break_kee]"
                  type="number"
                  :min="0"
                  :append="$config.currency"
                />
              </template>
            </el-table-column>
          </el-table>
        </template>

        <template v-if="form.registration_fee_type === 'bundle'">
          <el-table :data="form.feeRules" style="width: 100%" class="fee-rules-table bundle-fee-tables">
            <el-table-column :label="$tc('form.description')" class-name="bundle-fee-desc-col">
              <template slot-scope="scope">
                <span class="bundle-rule-desc-text">{{ bundleFeeRuleLabel(scope.$index) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$tc('form.price')" width="200px" class-name="bundle-fee-price-col">
              <template slot-scope="scope">
                <main-input
                  v-model.number="scope.row.price"
                  type="number"
                  :min="0"
                  :append="$config.currency"
                />
              </template>
            </el-table-column>
            <el-table-column
              :label="$tc('form.discount')"
              width="170px"
              align="right"
              class-name="bundle-fee-discount-col"
            >
              <template slot-scope="scope">
                <main-input
                  v-model.number="scope.row.price_break"
                  type="number"
                  :min="0"
                  :append="$config.currency"
                />
              </template>
            </el-table-column>
            <el-table-column width="60px" align="right" class-name="bundle-fee-delete-col">
              <template slot-scope="scope">
                <el-button
                  v-if="scope.$index === form.feeRules.length - 1"
                  type="text"
                  icon="el-icon-delete"
                  @click="removeFeeRule(scope.$index)"
                />
              </template>
            </el-table-column>
          </el-table>
          <div v-if="!form.feeRules || form.feeRules.length === 0" class="fee-rules-empty">{{ $tc('general.no_data') }}</div>
          <el-button type="default" icon="el-icon-plus" size="small" @click="addFeeRule" class="add-fee-rule-btn">{{ $tc('tournament.add_fee_rule') }}</el-button>
          <el-table :data="registrationFeeDataTeamOnly" style="width: 100%" class="fee-rules-table bundle-team-fee-table bundle-fee-tables">
            <el-table-column :label="$tc('form.description')" class-name="bundle-fee-desc-col">
              <template slot-scope="scope">
                {{ scope.row.desc }}
              </template>
            </el-table-column>
            <el-table-column :label="$tc('form.price')" width="200px" class-name="bundle-fee-price-col">
              <template slot-scope="scope">
                <main-input
                  v-model.number="form[scope.row.key]"
                  :validator="$v.form[scope.row.key]"
                  type="number"
                  :min="0"
                  :append="$config.currency"
                />
              </template>
            </el-table-column>
            <el-table-column
              :label="$tc('form.discount')"
              width="170px"
              align="right"
              class-name="bundle-fee-discount-col"
            >
              <template slot-scope="scope">
                <main-input
                  v-model.number="form[scope.row.break_kee]"
                  :validator="$v.form[scope.row.break_kee]"
                  type="number"
                  :min="0"
                  :append="$config.currency"
                />
              </template>
            </el-table-column>
            <el-table-column width="60px" align="right" class-name="bundle-fee-delete-col" />
          </el-table>
        </template>
      </panel>

      <panel>
        <div class="flex-between">
          <el-button @click="onCancel()">{{ $tc("general.cancel") }}</el-button>
          <el-button style="width: 400px" type="primary" @click="save()">{{
            isEdit ? $tc("general.edit") : $tc("general.create")
          }}</el-button>
        </div>
      </panel>
    </template>
  </div>
</template>

<script>
import errorHandler from '@/common/errorHandler.js'
import { required, integer } from 'vuelidate/lib/validators'
import DocumentTypesPanel from '@/components/documents/DocumentTypesPanel.vue'

export default {
  components: {
    DocumentTypesPanel
  },
  data () {
    return {
      form: {
        name: null,
        description: null,
        startDate: null,
        endDate: null,
        chargeAllDiscplines: false,
        disciplines: [],
        teamDisciplines: [],

        firstPlacePoints: null,
        secondPlacePoints: null,
        thirdPlacePoints: null,
        fourthPlacePoints: null,
        otherPlacementsPoints: null,
        single_fee_1: null,
        single_fee_1_break: null,
        single_fee_2: null,
        single_fee_2_break: null,
        single_fee_3: null,
        single_fee_3_break: null,
        single_fee_other: null,
        single_fee_other_break: null,
        team_fee: null,
        team_fee_break: null,
        registration_fee_type: null,
        price_per_discipline: null,
        price_per_discipline_break: null,
        team_discipline_fee: null,
        team_discipline_fee_break: null,
        feeRules: [],
        certificate: null,
        diploma: null,
        acknowledgement: null,
        accreditation: null
      },
      registrationFeeData: [
        {
          desc: this.$tc('settings.one_discipline'),
          key: 'single_fee_1',
          break_kee: 'single_fee_1_break'
        },
        {
          desc: this.$tc('settings.two_disciplines'),
          key: 'single_fee_2',
          break_kee: 'single_fee_2_break'
        },
        {
          desc: this.$tc('settings.three_disciplines'),
          key: 'single_fee_3',
          break_kee: 'single_fee_3_break'
        },
        {
          desc: this.$tc('settings.four_or_more_disciplines'),
          key: 'single_fee_other',
          break_kee: 'single_fee_other_break'
        },
        {
          desc: this.$tc('settings.team_disciplines'),
          key: 'team_fee',
          break_kee: 'team_fee_break'
        }
      ],

      cover: null,
      images: null,
      documents: null,
      error: false,
      loading: false
    }
  },

  computed: {
    isEdit () {
      return this.$route.name === 'EditTournament'
    },

    tournamentId () {
      return this.$route.params.id
    },

    tournament () {
      return this.isEdit
        ? this.$store.getters['tournaments/get'](this.tournamentId)
        : null
    },

    disciplineOptions () {
      return (
        this.disciplines
          ?.filter(
            (d) =>
              this.form.disciplines.includes(d.id) ||
              (!d.isArchived && !d.isDeleted)
          )
          ?.map((d) => ({
            id: d.id,
            text: d.name
          })) || []
      )
    },
    teamDisciplineOptions () {
      return (
        this.disciplines
          ?.filter(
            (d) =>
              this.form.teamDisciplines.includes(d.id) ||
              (!d.isArchived && !d.isDeleted)
          )
          ?.map((d) => ({
            id: d.id,
            text: d.name
          })) || []
      )
    },

    disciplines () {
      return this.$store.state.disciplines.data
    },

    registrationFeeTypeOptions () {
      return [
        {
          id: 'legacy',
          text: this.$tc('tournament.registration_fee_legacy')
        },
        {
          id: 'per_discipline',
          text: this.$tc('tournament.registration_fee_per_discipline')
        },
        {
          id: 'bundle',
          text: this.$tc('tournament.registration_fee_bundle')
        }
      ]
    },

    registrationFeeDataPerDiscipline () {
      return [
        {
          desc: this.$tc('tournament.price_per_discipline'),
          key: 'price_per_discipline',
          break_kee: 'price_per_discipline_break'
        },
        {
          desc: this.$tc('settings.team_disciplines'),
          key: 'team_discipline_fee',
          break_kee: 'team_discipline_fee_break'
        }
      ]
    },

    registrationFeeDataTeamOnly () {
      return [
        {
          desc: this.$tc('settings.team_disciplines'),
          key: 'team_discipline_fee',
          break_kee: 'team_discipline_fee_break'
        }
      ]
    }
  },

  created () {
    if (this.isEdit) {
      this.load()
    }
    this.setPageData()
  },

  watch: {
    'tournament.id': {
      handler () {
        this.initializeTournamentData()
      },
      immediate: true
    },
    'form.registration_fee_type' (newVal) {
      if (newVal === 'bundle' && (!this.form.feeRules || this.form.feeRules.length === 0)) {
        this.form.feeRules = this.form.feeRules || []
        this.form.feeRules.push({
          min_disciplines: 1,
          description: '',
          price: 0,
          price_break: 0
        })
      }
    }
  },

  methods: {
    initializeTournamentData () {
      if (this.tournament) {
        this.form = {
          name: this.tournament.name,
          description: this.tournament.description.replaceAll('<br />', ''),
          startDate: this.tournament.startDate,
          endDate: this.tournament.endDate,
          chargeAllDiscplines: this.tournament.chargeAllDiscplines || false,
          document: this.tournament.document,
          disciplines: this.tournament.disciplines?.map(
            (disc) => disc.disciplineId
          ),
          teamDisciplines: this.tournament.teamDisciplines?.map(
            (disc) => disc.disciplineId
          ),
          firstPlacePoints: this.tournament.firstPlacePoints,
          secondPlacePoints: this.tournament.secondPlacePoints,
          thirdPlacePoints: this.tournament.thirdPlacePoints,
          fourthPlacePoints: this.tournament.fourthPlacePoints,
          otherPlacementsPoints: this.tournament.otherPlacementsPoints,
          single_fee_1: this.tournament.single_fee_1,
          single_fee_1_break: this.tournament.single_fee_1_break,
          single_fee_2: this.tournament.single_fee_2,
          single_fee_2_break: this.tournament.single_fee_2_break,
          single_fee_3: this.tournament.single_fee_3,
          single_fee_3_break: this.tournament.single_fee_3_break,
          single_fee_other: this.tournament.single_fee_other,
          single_fee_other_break: this.tournament.single_fee_other_break,
          team_fee: this.tournament.team_fee,
          team_fee_break: this.tournament.team_fee_break,
          registration_fee_type: this.tournament.registration_fee_type ?? null,
          price_per_discipline: this.tournament.price_per_discipline ?? null,
          price_per_discipline_break: this.tournament.price_per_discipline_break ?? null,
          team_discipline_fee: this.tournament.team_discipline_fee ?? null,
          team_discipline_fee_break: this.tournament.team_discipline_fee_break ?? null,
          feeRules: (() => {
            const rules = this.tournament.feeRules || []
            if (this.tournament.registration_fee_type === 'bundle' && rules.length === 0) {
              return [{ min_disciplines: 1, description: '', price: 0, price_break: 0 }]
            }
            return rules.map(r => ({
              id: r.id,
              tournamentId: r.tournamentId,
              min_disciplines: r.min_disciplines,
              description: r.description ?? '',
              price: r.price,
              price_break: r.price_break ?? 0
            }))
          })(),
          certificate: null,
          diploma: null,
          acknowledgement: null,
          accreditation: null
        }
      }
    },

    onCancel () {
      if (this.isEdit) {
        return this.$router.push({
          name: 'TournamentView',
          params: { id: this.tournament.id }
        })
      }
      this.$router.push({ name: 'Tournaments' })
    },

    async load () {
      this.loading = true
      try {
        await this.$store.dispatch('tournaments/get', this.tournamentId)
        this.setPageData()
        this.error = false
      } catch (e) {
        console.error(e)
        this.error = true
      } finally {
        this.loading = false
      }
    },

    setPageData () {
      const breadcrumbs = [
        {
          text: this.$tc('tournament.tournament', 2),
          to: { name: 'Tournaments' }
        },
        ...(this.isEdit
          ? [
            {
              text: this.tournament?.name,
              to: {
                name: 'TournamentView',
                params: { id: this.tournament?.id }
              }
            }
          ]
          : []),
        {
          text: this.isEdit
            ? this.$tc('tournament.edit_tournament')
            : this.$tc('tournament.create_tournament')
        }
      ]

      this.$store.commit('app/set', {
        title: this.isEdit
          ? this.tournament?.name ?? this.$tc('tournament.edit_tournament')
          : this.$tc('tournament.create_tournament'),
        breadcrumbs
      })
    },

    async save () {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) return

      this.loading = true
      try {
        let tournament = this.tournament

        const newDisciplines = JSON.parse(
          JSON.stringify(this.form.disciplines)
        )
        const newTeamDisciplines = JSON.parse(
          JSON.stringify(this.form.teamDisciplines)
        )

        const toNumber = (value) => {
          if (value === '' || value === null || value === undefined) return undefined
          const n = Number(value)
          return Number.isFinite(n) ? n : undefined
        }

        const payload = {
          name: this.form.name,
          description: this.form.description,
          startDate: this.form.startDate,
          endDate: this.form.endDate || undefined,
          chargeAllDiscplines: this.form.chargeAllDiscplines,
          firstPlacePoints: this.form.firstPlacePoints,
          secondPlacePoints: this.form.secondPlacePoints,
          thirdPlacePoints: this.form.thirdPlacePoints,
          fourthPlacePoints: this.form.fourthPlacePoints,
          otherPlacementsPoints: this.form.otherPlacementsPoints,
          single_fee_1: toNumber(this.form.single_fee_1),
          single_fee_1_break: toNumber(this.form.single_fee_1_break),
          single_fee_2: toNumber(this.form.single_fee_2),
          single_fee_2_break: toNumber(this.form.single_fee_2_break),
          single_fee_3: this.form.single_fee_3,
          single_fee_3_break: this.form.single_fee_3_break,
          single_fee_other: toNumber(this.form.single_fee_other),
          single_fee_other_break: toNumber(this.form.single_fee_other_break),
          team_fee: toNumber(this.form.team_fee),
          team_fee_break: toNumber(this.form.team_fee_break),
          registration_fee_type: this.form.registration_fee_type,
          price_per_discipline: toNumber(this.form.price_per_discipline),
          price_per_discipline_break: toNumber(this.form.price_per_discipline_break) ?? 0,
          team_discipline_fee: toNumber(this.form.team_discipline_fee),
          team_discipline_fee_break: toNumber(this.form.team_discipline_fee_break) ?? 0
        }
        if (this.form.registration_fee_type === 'bundle') {
          const ruleCount = this.form.feeRules.length
          payload.feeRules = this.form.feeRules.map((r, index) => {
            const num = index + 1
            const isLast = index === ruleCount - 1
            const description = isLast ? `${num} ${this.$tc('tournament.bundle_fee_index_and_more')}` : String(num)
            return {
              id: r.id || undefined,
              min_disciplines: num,
              description,
              price: r.price,
              price_break: r.price_break ?? 0
            }
          })
        }

        if (this.isEdit) {
          tournament = await this.$store.dispatch('tournaments/update', {
            id: this.tournament.id,
            payload
          })
        } else {
          tournament = await this.$store.dispatch(
            'tournaments/create',
            payload
          )
        }

        await this.syncDisciplines(
          tournament.disciplines,
          newDisciplines,
          tournament
        )
        await this.syncDisciplines(
          tournament.teamDisciplines,
          newTeamDisciplines,
          tournament,
          true
        )

        if (this.documents?.length) {
          const newDocumentsPromise = this.documents.map((document) =>
            this.$store
              .dispatch('documents/create', {
                path: document,
                tournamentId: tournament.id,
                name: document.name,
                size: document.size
              })
              .then((obj) =>
                this.$store.dispatch('documents/upload', {
                  id: obj.id,
                  file: document
                })
              )
          )

          await Promise.all(newDocumentsPromise)
        }

        if (this.images?.length) {
          const newImagesPromise = this.images.map((image) =>
            this.$store
              .dispatch('tournamentImages/create', {
                path: image,
                tournamentId: tournament.id,
                name: image.name,
                size: image.size
              })
              .then((obj) =>
                this.$store.dispatch('tournamentImages/upload', {
                  id: obj.id,
                  file: image
                })
              )
          )

          await Promise.all(newImagesPromise)
        }

        if (this.cover) {
          tournament = await this.$store.dispatch('tournaments/upload', {
            id: tournament.id,
            file: this.cover
          })
        }

        // Document types (certificates/diplomas/etc.) are managed via DocumentTypesPanel
        // No need to save them to database here

        this.updateActiveTournament(tournament)

        this.$message.success(
          `Uspešno ste ${this.isEdit ? 'izmenili' : 'napravili'} turnir.`
        )
        this.$emit(this.isEdit ? 'add' : 'edit')
      } catch (error) {
        console.error(error)
        errorHandler(error)
      } finally {
        this.loading = false
      }
    },

    updateActiveTournament (tournament) {
      if (this.activeTournament?.id === tournament.id || tournament.isActive) {
        this.$store.commit(
          'app/setActiveTournament',
          tournament.isActive ? tournament : null
        )
      }
    },

    addFeeRule () {
      this.form.feeRules.push({
        min_disciplines: this.form.feeRules.length + 1,
        description: '',
        price: 0,
        price_break: 0
      })
    },

    bundleFeeRuleLabel (index) {
      const total = this.form.feeRules.length
      if (!total || index < 0 || index >= total) return '—'
      const num = index + 1
      const isLast = index === total - 1
      return isLast ? `${num} ${this.$tc('tournament.bundle_fee_index_and_more')}` : String(num)
    },

    removeFeeRule (index) {
      this.form.feeRules.splice(index, 1)
    },

    syncDisciplines (currentDisciplines, newIds, tournament, isTeam) {
      const currentIds =
        currentDisciplines?.map((disc) => disc.disciplineId) || []

      const toDelete = currentIds.filter((id) => !newIds.includes(id))
      const toCreate = newIds.filter((id) => !currentIds.includes(id))

      const toDeleteIds = toDelete.map((id) =>
        currentDisciplines.find((disc) => id === disc.disciplineId)
      )

      const storePrefix = isTeam
        ? 'tournamentTeamDisciplines'
        : 'tournamentDisciplines'

      const deletePromises =
        toDeleteIds.map((disc) =>
          this.$store.dispatch(`${storePrefix}/delete`, disc.id)
        ) || []

      const createPromises =
        toCreate.map((disciplineId) => {
          return this.$store.dispatch(`${storePrefix}/create`, {
            disciplineId,
            tournamentId: tournament.id
          })
        }) || []

      return Promise.all([...deletePromises, ...createPromises])
    }
  },

  validations () {
    return {
      form: {
        name: { required },
        description: { required },
        startDate: { required },
        disciplines: { required },
        teamDisciplines: { required },
        single_fee_1: { integer },
        single_fee_1_break: { integer },
        single_fee_2: { integer },
        single_fee_2_break: { integer },
        single_fee_3: { integer },
        single_fee_3_break: { integer },
        single_fee_other: { integer },
        single_fee_other_break: { integer },
        team_fee: { integer },
        team_fee_break: { integer },
        firstPlacePoints: { integer },
        secondPlacePoints: { integer },
        thirdPlacePoints: { integer },
        fourthPlacePoints: { integer },
        otherPlacementsPoints: { integer }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.scoring-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.scoring-labels,
.scoring-inputs {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  align-items: start;
}

.scoring-labels span {
  font-size: 16px;
  margin-bottom: 5px;
}

.scoring-inputs ::v-deep .form-group {
  margin: 0;
}
</style>
